import styles from 'css/src/components/public-pages/ContentfulRichText.module.scss';

import React from 'react';
import { documentToReactComponents, type Options } from '@contentful/rich-text-react-renderer';
import type { Block, Document, Inline } from '@contentful/rich-text-types';

const TABLE: string = 'table';
const TABLE_ROW: string = 'table-row';
const TABLE_CELL: string = 'table-cell';
const TABLE_HEADER_CELL: string = 'table-header-cell';

const isHeaderRow = (row: Block | Inline): boolean =>
    row.content.length > 0 && row.content.every((cell) => cell.nodeType === TABLE_HEADER_CELL);

const countLeadingHeaderRows = (table: Block | Inline): number => {
    const firstBodyRowIndex = table.content.findIndex(
        (row) => !('content' in row) || !isHeaderRow(row),
    );
    return firstBodyRowIndex === -1 ? table.content.length : firstBodyRowIndex;
};

const richTextOptions: Options = {
    renderNode: {
        [TABLE]: (node, children) => {
            const rows = React.Children.toArray(children);
            const headerRowCount = countLeadingHeaderRows(node);
            const headerRows = rows.slice(0, headerRowCount);
            const bodyRows = rows.slice(headerRowCount);
            return (
                <div
                    className={styles['table-scroll']}
                    role="region"
                    aria-label="Table"
                    tabIndex={0}
                >
                    <table className={styles['table']}>
                        {headerRows.length > 0 && <thead>{headerRows}</thead>}
                        {bodyRows.length > 0 && <tbody>{bodyRows}</tbody>}
                    </table>
                </div>
            );
        },
        [TABLE_ROW]: (_node, children) => <tr>{children}</tr>,
        [TABLE_HEADER_CELL]: (_node, children) => <th scope="col">{children}</th>,
        [TABLE_CELL]: (_node, children) => <td>{children}</td>,
    },
};

interface ContentfulRichTextProps {
    document: Document;
}

const ContentfulRichText: React.FC<ContentfulRichTextProps> = ({ document }) => (
    <>{documentToReactComponents(document, richTextOptions)}</>
);

export default ContentfulRichText;
